import { all, call, put, select, takeLatest, take } from 'redux-saga/effects';
import EventNames from 'src/lib/eventNames';
import appDux from 'src/redux/app';
import uiDux from 'src/redux/ui';
import macheteDux from 'src/redux/machete';
import trackingDux from 'src/redux/tracking';
import undoDux from 'src/redux/undo';
import notificationDux from 'src/redux/notification';
import { actions as macheteActions, getModel } from 'src/machete-redux';
import constants from 'src/constants';
import utils from 'src/utils';
import featureFlags from 'src/featureFlags';

const {
  MATERIALS,
  SASH_ID,
  UNKNOWN_ID,
  SLIDING_GLASS_DOOR_ID,
  FEATURE_API_MATERIAL_IDS,
} = constants.labels;
const { extractWindowNodeData } = utils.app;
const {
  generateStyleUpdate,
  generatePaintColorUpdate,
  generateDoorOrWindowUpdate,
} = utils.designSidebar;
const { DESIGN_FEATURE_CLEAR_PAINT } = EventNames.viewer;

/**
 *
Load the node data from machete nodes and store the things we want
to keep track of:

- Material
- Brand
- Product
- Color
- uuid

And the unique combinations of the above

Initially when the model loads from machete, we only have primary_label_id,
which is equivalent to the label_id / material.
 */
export const extractMacheteNodeData = (labels, nodeData) => {
  const { uuid, properties } = nodeData;
  const {
    primary_label_id: { value: materialId },
    texture_id: { value: materialTextureId },
    color_hex: { value: materialColorHex },
  } = properties;

  // show unknown materials in the wall category
  if (materialId === -1) {
    return {
      uuid,
      category: 'walls',
      materialId: UNKNOWN_ID,
    };
  }

  const windowAttributes =
    materialId === SASH_ID ? extractWindowNodeData(uuid) : {};

  const { category } = labels[materialId];

  return {
    uuid,
    category,
    materialId,
    materialColorHex,
    materialTextureId,
    ...windowAttributes,
  };
};

export function* initNodeStylesSaga() {
  yield all([
    take(appDux.actions.setDesignProInitComplete),
    take(appDux.actions.setProductCatalogInitialized),
  ]);

  yield call(setNodeStylesSaga);
}

export function* setNodeStylesSaga() {
  const sagaToUseForSettingNodeStyles = featureFlags.shouldUseMacheteFeatureAPI()
    ? setNodeStylesSaga_useFeatureApi
    : setNodeStylesSaga_legacy;

  yield call(sagaToUseForSettingNodeStyles);
}

// eslint-disable-next-line camelcase
export function* setNodeStylesSaga_useFeatureApi() {
  const model = getModel();
  const labels = yield select(macheteDux.selectors.getLabels);
  yield call([model, model.initFeatureAPI], model.segmentationTree, labels);
  const features = yield call([
    model.featureAPI,
    model.featureAPI.findFeaturesByAttribute,
  ]);
  const styles = features
    .filter(({ materialId }) => !!MATERIALS[materialId])
    .map((feature) => ({
      uuid: feature.uuid,
      category: feature.category,
      materialId: feature.materialId,
      materialColorHex: feature.materialColorHex,
      materialTextureId: feature.materialTextureId,
      frameType: feature.frameType,
      gridType: feature.gridType,
    }));

  yield put(appDux.actions.setNodeStyles(styles));
}

export function* setNodeStylesSaga_legacy() {
  const nodes = yield select(macheteDux.selectors.getVisibleNodes);
  const labels = yield select(macheteDux.selectors.getLabels);
  const styles = nodes.map(extractMacheteNodeData.bind(null, labels));

  yield put(appDux.actions.setNodeStyles(styles));
}

export function* addSplitNodeStylesSaga({
  payload: { nodes, styleAttributes },
}) {
  const styles = yield select(appDux.selectors.getNodeStyles);
  const newStyles = nodes.map((uuid) => ({ uuid, ...styleAttributes }));

  yield put(appDux.actions.setNodeStyles(styles.concat(newStyles)));
  yield call(applyAttributeToNodes, nodes, styleAttributes);
}

/**
 * Gets the texture for material choices
 */
export function* findTextureForMaterialSaga({ materialId, materialTextureId }) {
  const labels = yield select(macheteDux.selectors.getLabels);
  const products = yield select(macheteDux.selectors.getProducts);
  let texture;

  if (materialTextureId) {
    // TODO: consistify getting textures for products/materials/etc.
    texture = {
      textureData: products[materialTextureId],
    };
  } else {
    texture = labels[materialId];
  }

  // a null value for color will reset it
  return { texture, color: null };
}

/**
 * Gets the texture for product choices
 */
export function* findTextureForProductSaga({ productId }) {
  const products = yield select(appDux.selectors.getMacheteProducts);
  const product = products[productId];
  // a null value for color will reset it
  return { texture: product, color: null };
}

/**
 * Gets the texture for color choices
 */
export function* findTextureForColorSaga({ colorId }) {
  const variations = yield select(appDux.selectors.getMacheteVariations);
  const variation = variations[colorId] || {};

  // a null value for color will reset it
  return {
    color: variation.color || null,
    texture: variation,
  };
}

export function* findColorForPaintSaga({ paintColorId }) {
  const variations = yield select(appDux.selectors.getMacheteVariations);
  const variation = variations[paintColorId] || {};

  // a null value for color will reset it
  return variation.color || null;
}

/**
 * Gets the color and texture for a given style update
 */
export function* findTextureAndColorForUpdateSaga(update) {
  let texture;
  let color;

  // Having certain style properties will determine where/how we retrieve the texture and color
  // Keep the following conditionals in this order for specificity
  if (update.materialId || update.materialTextureId)
    ({ texture, color } = yield call(findTextureForMaterialSaga, update));
  if (update.productId)
    ({ texture, color } = yield call(findTextureForProductSaga, update));
  if (update.colorId)
    ({ texture, color } = yield call(findTextureForColorSaga, update));
  if (update.materialColorHex) color = update.materialColorHex;
  if (update.paintColorId) color = yield call(findColorForPaintSaga, update);

  // always return texture/color because a null value for color will reset it
  return { texture, color };
}

export function* updateDoorSaga(nodes, update, color) {
  const model = getModel();

  // eslint-disable-next-line no-restricted-syntax
  for (const uuid of nodes) {
    const feature = model.featureAPI.getFeatureByUUID(uuid);

    yield call([feature, feature.setAttribute], {
      material_id: update.materialId,
    });
    yield call([feature, feature.setAttribute], { color });
    yield call([feature, feature.setAttribute], {
      parametric_options: { grilleType: update.gridType },
    });
  }
}

export function* applyAttributeToNodes(nodes, update) {
  const { getNodeStylesMap, getIsEditing3DOpening } = appDux.selectors;
  const nodeStylesMap = yield select(getNodeStylesMap);
  const isEditing3DOpening = yield select(getIsEditing3DOpening);
  const style = nodeStylesMap[nodes[0]];
  const { color, texture } = yield call(
    findTextureAndColorForUpdateSaga,
    update,
  );

  if (isEditing3DOpening) {
    // if a window selection, update element geometry
    // apply the texture to the element
    if (style.materialId === SASH_ID) {
      yield put(
        macheteDux.actions.updateElementWindowGeometry(nodes, update, color),
      );
      return;
    }

    if (style.materialId === SLIDING_GLASS_DOOR_ID) {
      yield call(updateDoorSaga, nodes, update, color);
    }
  } else if (color || texture) {
    const actionCreator = FEATURE_API_MATERIAL_IDS.includes(update.materialId)
      ? macheteDux.actions.updateFeatureAttributes
      : macheteDux.actions.updateElementAttributes;
    yield put(
      actionCreator(nodes, {
        texture,
        color,
        materialId: style.materialId,
      }),
    );
  }
}

/**
 * Triggers applying the textures to model element when a user deletes a split
 * or when a user clicks on an item in the extended sidebar menu.
 * If it's a color property and window material, trigger geometry update
 * Sets the updated node styles after applying the selection
 * Updates active editing style and attribute with the selection data
 */
export function* replaceNodeStylesSaga({ payload: { nodes, newStyle } }) {
  const currentNodeStyles = yield select(appDux.selectors.getNodeStyles);
  // replace the node values with the new selection
  const updatedNodesStyles = currentNodeStyles.map((nodeStyle) => {
    if (!nodes.includes(nodeStyle.uuid)) return nodeStyle;
    return {
      ...newStyle,
      uuid: nodeStyle.uuid,
    };
  });
  yield put(appDux.actions.setNodeStyles(updatedNodesStyles));
  // update all the attributes on the nodes
  yield call(applyAttributeToNodes, nodes, newStyle);
}

/**
 * Handles clicking an element in the extended design sidebar. Gets the current
 * active style and attribute and triggers saga to apply the selected
 * item the active attribute of the style's nodes. Updates the active
 * style so extended sidebar data stays fresh
 */
export function* handleAttributeSelectionClickSaga(action) {
  const {
    payload: { selection },
  } = action;
  const {
    getActiveEditingStyle,
    getActiveEditingAttribute,
    getIsEditing3DOpening,
    getMacheteProducts,
  } = appDux.selectors;

  const activeStyle = yield select(getActiveEditingStyle);
  const activeAttribute = yield select(getActiveEditingAttribute);
  const isEditing3DOpening = yield select(getIsEditing3DOpening);
  const products = yield select(getMacheteProducts);
  const paintColorUpdate = generatePaintColorUpdate(
    activeAttribute,
    products,
    selection,
  );
  const update = generateStyleUpdate(activeAttribute.property, selection.id);

  const manufacturerProducts = yield select(
    macheteDux.selectors.getManufacturerProducts,
  );

  if (isEditing3DOpening) {
    const { texture } = yield call(findTextureAndColorForUpdateSaga, {
      colorId: selection.id,
    });
    // intermediate variable is used here becauae the decon syntax was really confusing
    const data = generateDoorOrWindowUpdate(manufacturerProducts, texture);

    update.frameType = data.frameType;
    update.gridType = data.gridType;
  }
  const updatedActiveStyle = { ...activeStyle, ...update, ...paintColorUpdate };
  yield call(
    updateNodeViewOnAttributeClick,
    activeStyle,
    activeAttribute,
    updatedActiveStyle,
    update,
  );

  yield call(
    updateUndoRedoStackOnAttributeClick,
    activeStyle,
    updatedActiveStyle,
    activeAttribute,
    action,
  );
}

export function* updateNodeViewOnAttributeClick(
  activeStyle,
  activeAttribute,
  updatedActiveStyle,
  update,
) {
  // update the active style
  // update node view in model
  updatedActiveStyle.styleKey = appDux.selectors.generateStyleKey(
    updatedActiveStyle,
  );
  // get only the style attributes out of it
  const { nodes, ...styleAttributes } = updatedActiveStyle;
  // mix the current style and the updates
  const nodeStyleUpdate = { ...styleAttributes, ...update };
  // replace node style with the current + update mix
  yield put(
    appDux.actions.replaceNodeStyles(activeStyle.nodes, nodeStyleUpdate),
  );

  yield put(
    trackingDux.actions.trackAttributeSelection({
      elementStyle: updatedActiveStyle,
      attributeProperty: activeAttribute.property,
    }),
  );
}

export function* updateUndoRedoStackOnAttributeClick(
  activeStyle,
  updatedStyle,
  activeAttribute,
  action,
) {
  // update undo/redo stack
  // if a next attribute exists:
  //   - make that one the active one if it's available for selection
  //   - otherwise close the extended sidebar
  // if no next attribute exists, stay on the current
  const nextAttribute = yield select(appDux.selectors.getNextActiveAttribute);
  const nextActiveAttribute = nextAttribute || activeAttribute;

  yield put(
    appDux.actions.setActiveEditingStyle(updatedStyle, nextActiveAttribute),
  );

  // record undo/redo action, unless the active attribute is a brand.
  // this is because selecting brand doesn't visually change anything, so
  // we don't want to record an undo action for it
  if (!activeAttribute.property.match(/brand/i)) {
    yield put(
      undoDux.actions.recordUndoableAction({
        originalType: action.type,
        data: {
          currentStyle: activeStyle,
          newStyle: updatedStyle,
          currentAttribute: activeAttribute,
          newAttribute: nextActiveAttribute,
        },
      }),
    );
  }
}

/**
 * Updates selected elements by seeing which nodes match all the
 * properties of the action's filterObj
 */
function* updateSelectedElementsByAttributeTypeSaga({ payload }) {
  const { filterObject } = payload;
  const styles = yield select(appDux.selectors.getNodeStyles);
  const selectedNodeUUIDs = styles
    .filter((style) => {
      const filterAttributes = Object.keys(filterObject);
      return filterAttributes.every(
        (attr) => style[attr] === filterObject[attr],
      );
    })
    .map(({ uuid }) => uuid);

  yield put(macheteActions.segmentation.setNodeSelection(selectedNodeUUIDs));
}

/**
 *
 * When design style is clicked, set the selected nodes to those nodes in
 * the style's `nodes` array
 */
export function* selectNodesInStyleSaga({ payload: { style } }) {
  yield put(appDux.actions.setBreadcrumbData({}));
  yield put(macheteActions.segmentation.setNodeSelection(style.nodes));
}

export function* handleSetIsSelectToolActive() {
  const isSelectToolActive = yield select(appDux.selectors.isSelectToolActive);
  const notificationType = isSelectToolActive
    ? constants.notification.ENABLE_SELECT_TOOL_NOTIFICATION
    : constants.notification.DISABLE_SELECT_TOOL_NOTIFICATION;
  yield put(notificationDux.actions.handleNotification(notificationType));
}

export function* handleClearPaintClickSaga(action) {
  const activeAttribute = yield select(
    appDux.selectors.getActiveEditingAttribute,
  );
  // The reason this is the same activeAttribute is because when we are clearing paint
  // there is no `nextActiveAttribute` and in `handleAttributeSelectionClickSaga` we
  // do this: `const `\nextActiveAttribute = nextAttribute || activeAttribute;`
  const nextActiveAttribute = activeAttribute;
  const activeStyle = action.payload.style;
  const { nodes, ...styleAttributes } = activeStyle;
  const clearPaintUpdate = {
    paintBrandId: '',
    paintProductId: '',
    paintColorId: '',
  };
  const nextActiveStyle = { ...activeStyle, ...clearPaintUpdate };
  const newNodeStyles = { ...styleAttributes, ...clearPaintUpdate };

  // record undo/redo action, unless the active attribute is a brand.
  // this is because selecting brand doesn't visually change anything, so
  // we don't want to record an undo action for it
  yield put(
    undoDux.actions.recordUndoableAction({
      originalType: action.type,
      data: {
        currentStyle: activeStyle,
        newStyle: nextActiveStyle,
        currentAttribute: activeAttribute,
        newAttribute: nextActiveAttribute,
      },
    }),
  );

  yield put(trackingDux.actions.track(DESIGN_FEATURE_CLEAR_PAINT));
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(appDux.actions.replaceNodeStyles(nodes, newNodeStyles));
}

// if extended sidebar close and brand selected w/o product, unselect the brand
export function* handleCloseExtendedSidebarSaga() {
  const styleChanges = {};
  const activeStyle = yield select(appDux.selectors.getActiveEditingStyle);
  const { nodes, ...style } = activeStyle;

  // for some reason closeExtendedSidebar is fired on app start, so we will
  // return early here if there are no style attributes
  if (!Object.keys(style).length) return;

  // if brand prop is set and corresponding product prop is NOT set, then
  // unset the brand property. See NS-1925
  [
    ['brandId', 'productId'],
    ['paintBrandId', 'paintColorId'],
  ].forEach(([brandAttr, productAttr]) => {
    if (!!style[brandAttr] && !style[productAttr]) {
      styleChanges[brandAttr] = '';
    }
  });

  if (Object.keys(styleChanges).length) {
    const update = { ...style, ...styleChanges };
    update.styleKey = appDux.selectors.generateStyleKey(update);

    yield put(appDux.actions.replaceNodeStyles(nodes, update));
  }
}

export default function* sagas() {
  yield all([
    takeLatest(
      uiDux.actions.handleAttributeSelectionClick,
      handleAttributeSelectionClickSaga,
    ),
    takeLatest(uiDux.actions.handleClearPaintClick, handleClearPaintClickSaga),
    takeLatest(uiDux.actions.handleDesignStyleClick, selectNodesInStyleSaga),
    takeLatest(
      appDux.actions.selectElementsByAttribute,
      updateSelectedElementsByAttributeTypeSaga,
    ),
    takeLatest(appDux.actions.addSplitNodeStyles, addSplitNodeStylesSaga),
    takeLatest(
      appDux.actions.setIsSelectToolActive,
      handleSetIsSelectToolActive,
    ),
    takeLatest(appDux.actions.replaceNodeStyles, replaceNodeStylesSaga),
    takeLatest(
      uiDux.actions.closeExtendedSidebar,
      handleCloseExtendedSidebarSaga,
    ),
  ]);
}
